import type { DateSerializable } from '@vs-shop/schemas/DateSerializable';
import {
  calculateDisplayQuantity,
  isInfiniteStock,
  isLastItem,
} from '@vs-shop/domain/stock/stockQuantityUtils';

import { ProductStockQuantity } from '@vs-shop/commerce-ui/productStock';

import { getMemoizedIntlShapeWithDefaultLocale } from '../../../../../../utils/intl';

import { IntlDate, IntlDecimal } from '../../../../intl/Intl';

type StockQuantityProps = {
  availableQuantity: number;
  isPreOrder?: boolean;
  isSupplierStock?: boolean;
  showStockPrefix?: boolean;
  dispatchDate?: DateSerializable | null;
};

/**
 * How much of a product rcprofi says is to be had, in Czech.
 *
 * Four sentences over one number, in the order the storefront prefers them: an
 * awaited date beats a count, the supplier's shelf beats a count, one piece
 * left is said in words, and anything else is counted in brackets. Unbounded
 * stock says nothing at all — no element, so the label ends after its source.
 *
 * `showStockPrefix` is the tooltip trigger's shape: a line read on its own
 * needs the word "Skladem", while a line following "E-shop" already has it.
 */
export function StockQuantity({
  availableQuantity,
  isPreOrder = false,
  isSupplierStock = false,
  showStockPrefix = false,
  dispatchDate = null,
}: StockQuantityProps) {
  const intl = getMemoizedIntlShapeWithDefaultLocale();

  if (isInfiniteStock(availableQuantity)) {
    return null;
  }

  if (isPreOrder && dispatchDate !== null) {
    return (
      <ProductStockQuantity>
        {intl.formatMessage(
          {
            id: 'component.server.page.product.panel.productStockItem.preorder',
            defaultMessage: 'Skladem {date}',
          },
          { date: <IntlDate key="preorderDispatchDate" value={dispatchDate} /> }
        )}
      </ProductStockQuantity>
    );
  }

  if (isSupplierStock) {
    return (
      <ProductStockQuantity>
        {intl.formatMessage({
          id: 'common.eshop.atTheSuppliers',
          defaultMessage: 'U dodavatele',
        })}
      </ProductStockQuantity>
    );
  }

  if (isLastItem(availableQuantity)) {
    return (
      <ProductStockQuantity>
        {showStockPrefix
          ? intl.formatMessage({
              id: 'component.server.product.card.productCardStockTooltip.lastItem',
              defaultMessage: 'Skladem poslední kus',
            })
          : intl.formatMessage({
              id: 'component.server.page.product.panel.productStockItem.quantity.lastItem',
              defaultMessage: 'poslední kus',
            })}
      </ProductStockQuantity>
    );
  }

  const displayQuantity = calculateDisplayQuantity(availableQuantity);
  const quantity =
    typeof displayQuantity === 'number' ? (
      <IntlDecimal key="quantity" value={displayQuantity} />
    ) : (
      displayQuantity
    );

  return (
    <ProductStockQuantity>
      {showStockPrefix
        ? intl.formatMessage(
            {
              id: 'component.server.product.card.productCardStockTooltip.stock',
              defaultMessage: 'Skladem {quantity} ks',
            },
            { quantity }
          )
        : intl.formatMessage(
            {
              id: 'component.server.page.product.panel.productStockItem.quantity',
              defaultMessage: '{quantity} ks',
            },
            { quantity }
          )}
    </ProductStockQuantity>
  );
}
